import { ScaleType, type TrackableData, type TrackableType } from "@qendo/trackables";
import type { DateArg } from "date-fns";
import type { LineChartPropsType } from "gifted-charts-core";
import type { OptionalNumeric } from "~/util/string";

export enum ReportingPeriod {
    all = "all",
    daily = "daily",
    last_week = "last_week",
    weekly = "weekly",
    monthly = "monthly",
    yearly = "yearly",
}

export type ReportableScale =
    | ScaleType.ovulation
    | ScaleType.sleep
    | ScaleType.mood
    | ScaleType.temperature
    | ScaleType.ten
    | ScaleType.twenty
    | ScaleType.thirty
    | ScaleType.water;

export const ReportableScales: ReportableScale[] = [
    ScaleType.ovulation,
    ScaleType.sleep,
    ScaleType.mood,
    ScaleType.temperature,
    ScaleType.ten,
    ScaleType.twenty,
    ScaleType.thirty,
    ScaleType.water,
];

export type ReportablePeriod =
    | ReportingPeriod.weekly
    | ReportingPeriod.last_week
    | ReportingPeriod.monthly
    | ReportingPeriod.yearly;

export const ReportablePeriods: ReportablePeriod[] = [
    ReportingPeriod.weekly,
    ReportingPeriod.monthly,
    ReportingPeriod.yearly,
];

export const ReportingPeriodMap: Record<ReportingPeriod, string> = {
    [ReportingPeriod.all]: "All time",
    [ReportingPeriod.daily]: "Daily",
    [ReportingPeriod.last_week]: "Last week",
    [ReportingPeriod.weekly]: "Weekly",
    [ReportingPeriod.monthly]: "Monthly",
    [ReportingPeriod.yearly]: "Yearly",
};

export enum YAxisScale {
    numeric,
    mood,
    temperature,
    water,
}

export const YAxisScaleMap: Record<ReportableScale, YAxisScale> = {
    [ScaleType.ovulation]: YAxisScale.numeric,
    [ScaleType.sleep]: YAxisScale.numeric,
    [ScaleType.mood]: YAxisScale.mood,
    [ScaleType.temperature]: YAxisScale.temperature,
    [ScaleType.ten]: YAxisScale.numeric,
    [ScaleType.twenty]: YAxisScale.numeric,
    [ScaleType.thirty]: YAxisScale.numeric,
    [ScaleType.water]: YAxisScale.water,
};

export type PointData = {
    date: DateArg<Date>;
    average?: OptionalNumeric;
};

export type ReportSummaryData = {
    id: string;
    trackable_id: string;
    average?: OptionalNumeric;
    updated_at: DateArg<Date> & string;
};

export type ReportableTrackable = TrackableData<TrackableType, ReportableScale>;

export type ReportSelectorData = ReportSummaryData & ReportableTrackable;

export type ReportSeriesData = {
    trackable_id: string;
    average?: OptionalNumeric;
    points: PointData[];
};

export type ExtraReportsData = ReportSeriesData[] &
    (
        | []
        | [ReportSeriesData]
        | [ReportSeriesData, ReportSeriesData]
        | [ReportSeriesData, ReportSeriesData, ReportSeriesData]
        | [ReportSeriesData, ReportSeriesData, ReportSeriesData, ReportSeriesData]
    );

export type ReportData = {
    period: ReportablePeriod;
    start_at: DateArg<Date>;
    end_at: DateArg<Date>;
    compare?: ExtraReportsData;
} & ReportSeriesData;

export type ReportRequestData = {
    period: ReportablePeriod;
    trackable_id: string;
    from?: DateArg<Date> | null;
    compare?: string[];
};

export type ReportDataPropSet =
    | "data"
    | "spacing"
    | "zIndex"
    | "thickness"
    | "strokeDashArray"
    | "strokeLinecap"
    | "areaChart"
    | "stepChart"
    | "startIndex"
    | "endIndex"
    | "color"
    | "endFillColor"
    | "startOpacity"
    | "endOpacity"
    | "textColor"
    | "textFontSize"
    | "hideDataPoints"
    | "dataPointsHeight"
    | "dataPointsWidth"
    | "dataPointsRadius"
    | "dataPointsColor"
    | "dataPointsShape"
    | "showArrow"
    | "arrowConfig"
    | "lineSegments"
    | "renderTooltip";

export type RecordDataNumber = 1 | 2 | 3 | 4 | 5;
export type RecordDataKey<
    TRecord extends ReportDataPropSet,
    TKey extends RecordDataNumber = RecordDataNumber,
> = `${TRecord}${TKey}`;

export type ReportComponentProps = {
    report: ReportSelectorData;
    data: ReportData;
} & Omit<LineChartPropsType, `${ReportDataPropSet}${"" | RecordDataNumber}`>;

export type ReportColourProp = "color" | "dataPointsColor";

export type ReportColourProps = Pick<LineChartPropsType, RecordDataKey<ReportColourProp>>;
